/**
 * three.js centre visual for the processing console.
 *
 * The seven validation layers (Domain, Requirement, Concept, Relationship,
 * Consistency, Knowledge Quality, Governance) orbit a glowing RegO AI core on a
 * tilted ring. A layer ignites as the run clears it, a data packet flies from
 * the core to that layer, and a beam stays lit behind it. Labels are HTML overlaid on the canvas, positioned
 * each frame by projecting the node's world position, so text stays crisp.
 */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import {
  VALIDATION_LAYERS,
  readPalette,
  isDarkTheme,
  type PipelineRun,
} from "./processingPipeline";

const RING_RADIUS = 3.15;
const RING_TILT = -0.16; // radians
const NODE_COUNT = VALIDATION_LAYERS.length;

/** Fresnel rim glow for the core's outer shell. */
const GLOW_VERT = `
varying vec3 vNormal;
varying vec3 vView;
void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vView = normalize(-mv.xyz);
  gl_Position = projectionMatrix * mv;
}`;

// Rendered on the BackSide, so the geometry normal faces away from the camera:
// dot(normal, view) runs from -1 at the disc centre to 0 at the silhouette.
// Shifting by 1 gives 0 in the middle and 1 at the rim - an atmosphere halo
// rather than a solid disc.
const GLOW_FRAG = `
uniform vec3 uColor;
uniform float uIntensity;
varying vec3 vNormal;
varying vec3 vView;
void main() {
  float d = dot(normalize(vNormal), normalize(vView));
  float rim = clamp(1.0 + d, 0.0, 1.0);
  float a = pow(rim, 3.0) * uIntensity;
  gl_FragColor = vec4(uColor, a);
}`;

interface StageNode {
  group: THREE.Group;
  mesh: THREE.Mesh<THREE.OctahedronGeometry, THREE.MeshStandardMaterial>;
  halo: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  beam: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  accent: THREE.Color;
  baseY: number;
  phase: number;
}

export function PipelineScene3D({ run }: { run: PipelineRun }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  // The animation loop reads the live run through a ref so it is created once.
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    const mount = mountRef.current;
    const labelHost = labelsRef.current;
    if (!mount || !labelHost) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // The scene is drawn with the app's own theme tokens so it matches the
    // dashboard in both light and dark mode.
    const palette = readPalette();
    const dark = isDarkTheme();
    const P = {
      primary: palette.primary,
      foreground: palette.foreground,
      muted: palette.mutedForeground,
      chart: palette.chart,
      /** Accent for a stage, by its index into the chart palette. */
      stage: (i: number) => palette.chart[i % palette.chart.length],
    };
    // Additive blending only reads over a dark surface; on the light theme it
    // washes everything to white, so fall back to normal alpha blending.
    const BLEND = dark ? THREE.AdditiveBlending : THREE.NormalBlending;

    /* ── Renderer, scene, camera ── */
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth || 1, mount.clientHeight || 1);
    renderer.domElement.style.display = "block";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      48,
      (mount.clientWidth || 1) / (mount.clientHeight || 1),
      0.1,
      200,
    );
    // Raised so the tilted ring reads as an ellipse rather than edge-on.
    camera.position.set(0, 3.5, 8.6);
    camera.lookAt(0, 0, 0);

    scene.add(new THREE.AmbientLight(0xffffff, dark ? 0.55 : 0.95));
    const coreLight = new THREE.PointLight(new THREE.Color(P.primary), 90, 24, 2);
    scene.add(coreLight);
    const rimLight = new THREE.DirectionalLight(new THREE.Color(P.chart[0]), 1.1);
    rimLight.position.set(-4, 5, 3);
    scene.add(rimLight);

    /* ── Disposables ── */
    const disposables: { dispose(): void }[] = [];
    const track = <T extends { dispose(): void }>(o: T) => {
      disposables.push(o);
      return o;
    };

    /* ── Star field ── */
    const starCount = 900;
    const starPos = new Float32Array(starCount * 3);
    const starCol = new Float32Array(starCount * 3);
    // Tinted with the app's chart palette rather than arbitrary hues.
    const starColors = P.chart.map((c) => new THREE.Color(c));
    for (let i = 0; i < starCount; i++) {
      const r = 9 + Math.random() * 22;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPos[i * 3 + 1] = r * Math.cos(phi) * 0.55;
      starPos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      const col = starColors[i % starColors.length];
      starCol[i * 3] = col.r;
      starCol[i * 3 + 1] = col.g;
      starCol[i * 3 + 2] = col.b;
    }
    const starGeo = track(new THREE.BufferGeometry());
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute("color", new THREE.BufferAttribute(starCol, 3));
    const starMat = track(
      new THREE.PointsMaterial({
        size: 0.075,
        vertexColors: true,
        transparent: true,
        opacity: dark ? 0.85 : 0.5,
        depthWrite: false,
        blending: BLEND,
      }),
    );
    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    /* ── Core ── */
    const coreGroup = new THREE.Group();
    scene.add(coreGroup);

    const coreGeo = track(new THREE.SphereGeometry(0.72, 64, 64));
    const coreMat = track(
      new THREE.MeshStandardMaterial({
        color: new THREE.Color(P.primary),
        emissive: new THREE.Color(P.primary),
        emissiveIntensity: 1.35,
        roughness: 0.22,
        metalness: 0.15,
      }),
    );
    const core = new THREE.Mesh(coreGeo, coreMat);
    coreGroup.add(core);

    const shellGeo = track(new THREE.IcosahedronGeometry(0.98, 1));
    const shellMat = track(
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(P.chart[0]),
        wireframe: true,
        transparent: true,
        opacity: 0.32,
      }),
    );
    const shell = new THREE.Mesh(shellGeo, shellMat);
    coreGroup.add(shell);

    const glowGeo = track(new THREE.SphereGeometry(1.45, 48, 48));
    const glowMat = track(
      new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(P.primary) },
          uIntensity: { value: dark ? 0.95 : 0.45 },
        },
        vertexShader: GLOW_VERT,
        fragmentShader: GLOW_FRAG,
        transparent: true,
        blending: BLEND,
        side: THREE.BackSide,
        depthWrite: false,
      }),
    );
    coreGroup.add(new THREE.Mesh(glowGeo, glowMat));

    /* ── Orbit ring ── */
    const ringGroup = new THREE.Group();
    ringGroup.rotation.x = RING_TILT;
    scene.add(ringGroup);

    const trackGeo = track(new THREE.TorusGeometry(RING_RADIUS, 0.008, 8, 220));
    const trackMat = track(
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(P.muted),
        transparent: true,
        opacity: 0.28,
      }),
    );
    const trackMesh = new THREE.Mesh(trackGeo, trackMat);
    trackMesh.rotation.x = Math.PI / 2;
    ringGroup.add(trackMesh);

    /* ── Stage nodes ── */
    const nodeGeo = track(new THREE.OctahedronGeometry(0.36, 0));
    const haloGeo = track(new THREE.RingGeometry(0.5, 0.56, 48));

    const nodes: StageNode[] = VALIDATION_LAYERS.map((stage, i) => {
      const angle = (i / NODE_COUNT) * Math.PI * 2 - Math.PI / 2;
      const accent = new THREE.Color(P.stage(stage.accentIndex));

      const group = new THREE.Group();
      group.position.set(
        Math.cos(angle) * RING_RADIUS,
        0,
        Math.sin(angle) * RING_RADIUS,
      );
      ringGroup.add(group);

      const mat = track(
        new THREE.MeshStandardMaterial({
          color: new THREE.Color(dark ? 0x1e1e2d : 0xe2e8f0),
          emissive: accent.clone().multiplyScalar(0.06),
          emissiveIntensity: 1,
          roughness: 0.35,
          metalness: 0.5,
        }),
      );
      const mesh = new THREE.Mesh(nodeGeo, mat);
      group.add(mesh);

      const haloMat = track(
        new THREE.MeshBasicMaterial({
          color: accent,
          transparent: true,
          opacity: 0,
          side: THREE.DoubleSide,
          blending: BLEND,
          depthWrite: false,
        }),
      );
      const halo = new THREE.Mesh(haloGeo, haloMat);
      group.add(halo);

      // Beam from the core out to this stage, lit once the stage is reached.
      const beamGeo = track(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0, 0),
          group.position.clone(),
        ]),
      );
      const beamMat = track(
        new THREE.LineBasicMaterial({
          color: accent,
          transparent: true,
          opacity: 0,
          blending: BLEND,
          depthWrite: false,
        }),
      );
      const beam = new THREE.Line(beamGeo, beamMat);
      ringGroup.add(beam);

      return {
        group,
        mesh,
        halo,
        beam,
        accent,
        baseY: 0,
        phase: i * 0.9,
      };
    });

    /* ── Data packet that flies core -> active stage ── */
    const packetGeo = track(new THREE.SphereGeometry(0.075, 16, 16));
    const packetMat = track(
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(P.primary),
        transparent: true,
        opacity: 0,
        blending: BLEND,
        depthWrite: false,
      }),
    );
    const packet = new THREE.Mesh(packetGeo, packetMat);
    ringGroup.add(packet);

    /* ── Labels (HTML overlay) ── */
    const labelEls = VALIDATION_LAYERS.map((stage) => {
      const el = document.createElement("div");
      el.textContent = stage.label;
      el.style.cssText = [
        "position:absolute",
        "left:0",
        "top:0",
        "transform:translate(-50%,-50%)",
        "white-space:nowrap",
        "font-size:10px",
        "letter-spacing:.02em",
        "pointer-events:none",
        "transition:color .4s, opacity .4s",
        `color:${P.muted}`,
      ].join(";");
      labelHost.appendChild(el);
      return el;
    });

    /* ── Resize ── */
    const resize = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    /* ── Animation loop ── */
    const clock = new THREE.Clock();
    const projected = new THREE.Vector3();
    let raf = 0;
    let packetStage = -1;
    let packetT = 1;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      const dt = Math.min(clock.getDelta(), 0.05);
      const { layersLit, done } = runRef.current;
      // The layer currently being cleared (-1 once the run has finished).
      const current = done ? -1 : layersLit;

      // Core breathes; faster while work is still in flight.
      const pulse = 1 + Math.sin(t * (done ? 1.1 : 2.1)) * (done ? 0.03 : 0.055);
      core.scale.setScalar(pulse);
      shell.rotation.y += dt * 0.35;
      shell.rotation.x += dt * 0.14;
      coreMat.emissiveIntensity = 1.2 + Math.sin(t * 2.4) * 0.25;
      coreLight.intensity = (dark ? 80 : 45) + Math.sin(t * 2.4) * 22;

      if (!reduced) {
        ringGroup.rotation.y += dt * 0.12;
        stars.rotation.y += dt * 0.012;
      }

      // A newly reached stage launches a packet from the core.
      if (current !== packetStage && current < NODE_COUNT) {
        packetStage = current;
        packetT = 0;
      }
      if (packetT < 1 && packetStage >= 0) {
        packetT = Math.min(1, packetT + dt * 1.6);
        const target = nodes[packetStage].group.position;
        const eased = 1 - Math.pow(1 - packetT, 3);
        packet.position.copy(target).multiplyScalar(eased);
        packet.position.y = Math.sin(eased * Math.PI) * 0.55;
        packetMat.color.copy(nodes[packetStage].accent);
        packetMat.opacity = Math.sin(eased * Math.PI) * 0.95;
      } else {
        packetMat.opacity = 0;
      }

      nodes.forEach((n, i) => {
        // Lit strictly from the backend's cleared count: finishing the run is
        // not the same as clearing a layer.
        const lit = i < layersLit;
        const isActive = !done && layersLit > 0 && i === layersLit;

        // Ease each node toward its lit or dormant look.
        const targetEmissive = lit ? (isActive ? 1.5 : 0.85) : 0.06;
        const mat = n.mesh.material;
        mat.emissive.lerp(
          lit ? n.accent : n.accent.clone().multiplyScalar(0.06),
          0.08,
        );
        mat.emissiveIntensity +=
          (targetEmissive - mat.emissiveIntensity) * 0.09;
        mat.color.lerp(
          new THREE.Color(lit ? (dark ? 0x131320 : 0xffffff) : (dark ? 0x1e1e2d : 0xe2e8f0)),
          0.06,
        );

        n.mesh.rotation.y += dt * (isActive ? 1.5 : 0.45);
        n.mesh.rotation.x += dt * (isActive ? 0.9 : 0.22);

        const targetScale = isActive ? 1.42 : lit ? 1.08 : 0.86;
        n.mesh.scale.lerp(
          new THREE.Vector3(targetScale, targetScale, targetScale),
          0.1,
        );

        // Gentle bob so the ring never feels static.
        n.group.position.y = n.baseY + Math.sin(t * 1.1 + n.phase) * 0.11;

        // Halo pulses only on the active stage.
        n.halo.lookAt(camera.position);
        const haloMat = n.halo.material;
        haloMat.opacity += ((isActive ? 0.55 + Math.sin(t * 4) * 0.3 : 0) - haloMat.opacity) * 0.12;

        const beamMat = n.beam.material;
        beamMat.opacity += ((lit ? 0.45 : 0) - beamMat.opacity) * 0.08;

        // Project the node to screen space and park its label under it.
        projected.copy(n.group.position);
        n.group.parent?.localToWorld(projected);
        // View-space depth, used to dim labels on nodes hidden behind the core.
        const viewZ = projected.clone().applyMatrix4(camera.matrixWorldInverse).z;
        projected.project(camera);
        const w = mount.clientWidth || 1;
        const h = mount.clientHeight || 1;
        const x = (projected.x * 0.5 + 0.5) * w;
        const y = (-projected.y * 0.5 + 0.5) * h + 38;
        const el = labelEls[i];
        el.style.transform = `translate(-50%,-50%) translate(${x}px, ${y}px)`;
        el.style.color = lit ? P.foreground : P.muted;
        // Behind the core and overlapping it on screen -> fade so the labels
        // never pile up in the middle of the sphere.
        const fromCentre = Math.hypot(x - w / 2, y - h / 2);
        const occluded = viewZ < -camera.position.length() && fromCentre < 110;
        el.style.opacity =
          projected.z > 1 || occluded ? "0" : lit ? "1" : "0.75";
      });

      // Slow camera drift keeps the scene reading as 3D. Keep the same base
      // height as the initial placement so the ring stays an open ellipse.
      if (!reduced) {
        camera.position.x = Math.sin(t * 0.13) * 1.5;
        camera.position.y = 3.5 + Math.sin(t * 0.19) * 0.45;
        camera.lookAt(0, 0, 0);
      }

      renderer.render(scene, camera);
    };
    tick();

    /* ── Teardown ── */
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      labelEls.forEach((el) => el.remove());
      renderer.domElement.remove();
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <div className="relative h-full w-full">
      <div ref={mountRef} className="absolute inset-0" />
      <div ref={labelsRef} className="pointer-events-none absolute inset-0" />
    </div>
  );
}
